import * as React from "react";
import { Calendar as CalendarIcon, X as ClearIcon, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  parseLocalDate,
  formatLocalDate,
  formatTRDate,
  isValidDateString,
  getTurkishHolidays,
} from "@/utils/dateUtils";

// YYYY-MM-DD -> GG.AA.YYYY (elle giriş alanında gösterim)
const ymdToTR = (ymd) =>
  ymd && isValidDateString(ymd) ? formatTRDate(ymd, { short: true }) : "";

// Elle yazılan "GG.AA.YYYY" (. , / - ayraçları) -> YYYY-MM-DD. Geçersizse "".
const trTextToYmd = (text) => {
  if (!text) return "";
  const m = /^(\d{1,2})[.,/-](\d{1,2})[.,/-](\d{4})$/.exec(text.trim());
  if (!m) return "";
  const ymd = `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return isValidDateString(ymd) ? ymd : "";
};

/**
 * Tüm uygulamada tek tarih seçimi için kullanılan ortak component.
 * Görsel davranış LeaveRequestFormDialog'daki mevcut Popover+Calendar pattern'iyle uyumludur.
 *
 * Props:
 *   value: "YYYY-MM-DD" string veya "" / null
 *   onChange: (newValueStr) => void
 *   min, max: "YYYY-MM-DD" string (opsiyonel)
 *   label: string (opsiyonel)
 *   placeholder: string (varsayılan: "Tarih seçin")
 *   helperText: string (opsiyonel)
 *   error: string (opsiyonel, varsa kırmızı uyarı gösterilir)
 *   disabled: boolean
 *   required: boolean
 *   clearable: boolean (varsayılan true — boş bırakmaya izin verir)
 *   manualInput: boolean (varsayılan true) — elle (klavyeyle) tarih girişi alanı + takvim butonu gösterir
 *   highlightHolidays: boolean — Türkiye resmi tatillerini renkli göster
 *   align: PopoverContent align ("start" | "center" | "end")
 *   buttonClassName: ek class
 *   id: input id (label htmlFor için)
 */
const DatePicker = React.forwardRef(function DatePicker(
  {
    value = "",
    onChange,
    min,
    max,
    label,
    placeholder = "Tarih seçin",
    helperText,
    error,
    disabled = false,
    required = false,
    clearable = true,
    manualInput = true,
    highlightHolidays = false,
    align = "start",
    buttonClassName,
    id,
    ...rest
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);

  const selectedDate = React.useMemo(() => {
    if (!value || !isValidDateString(value)) return undefined;
    return parseLocalDate(value);
  }, [value]);

  const minDate = React.useMemo(
    () => (min && isValidDateString(min) ? parseLocalDate(min) : undefined),
    [min],
  );
  const maxDate = React.useMemo(
    () => (max && isValidDateString(max) ? parseLocalDate(max) : undefined),
    [max],
  );

  // disabled matcher — react-day-picker
  const disabledMatcher = React.useMemo(() => {
    const matchers = [];
    if (minDate) matchers.push({ before: minDate });
    if (maxDate) matchers.push({ after: maxDate });
    return matchers.length ? matchers : undefined;
  }, [minDate, maxDate]);

  // Takvimde gösterilen ay (kontrollü) — elle yazılan/seçilen değere atlar
  const [month, setMonth] = React.useState(
    () => (value && isValidDateString(value) ? parseLocalDate(value) : (minDate || new Date())),
  );
  React.useEffect(() => {
    if (value && isValidDateString(value)) setMonth(parseLocalDate(value));
  }, [value]);

  // Caption'daki ay/yıl açılır menüsü için yıl sınırları (hızlı yıl atlama)
  const fromYear = minDate ? minDate.getFullYear() : 2015;
  const toYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 5;

  // Resmi tatil modifier
  const modifiers = React.useMemo(() => {
    if (!highlightHolidays) return undefined;
    const years = new Set();
    const today = new Date();
    years.add(today.getFullYear());
    if (selectedDate) years.add(selectedDate.getFullYear());
    if (minDate) years.add(minDate.getFullYear());
    if (maxDate) years.add(maxDate.getFullYear());
    const holidayDates = [];
    years.forEach((y) => {
      (getTurkishHolidays(y) || []).forEach((iso) => {
        if (isValidDateString(iso)) holidayDates.push(parseLocalDate(iso));
      });
    });
    return { holiday: holidayDates };
  }, [highlightHolidays, selectedDate, minDate, maxDate]);

  const modifiersClassNames = React.useMemo(
    () => (highlightHolidays ? { holiday: "text-red-600 font-semibold" } : undefined),
    [highlightHolidays],
  );

  const displayLabel = React.useMemo(() => {
    if (!value || !isValidDateString(value)) return placeholder;
    return formatTRDate(value);
  }, [value, placeholder]);

  const handleSelect = React.useCallback(
    (date) => {
      if (!date) {
        if (clearable) onChange?.("");
        return;
      }
      onChange?.(formatLocalDate(date));
      setOpen(false);
    },
    [onChange, clearable],
  );

  const handleClear = React.useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      onChange?.("");
    },
    [onChange],
  );

  // Elle (klavyeyle) tarih girişi: kullanıcı serbestçe yazsın diye yerel metin
  // state'i tutulur; geçerli tarihe ulaşınca veya boşalınca onChange tetiklenir.
  const [text, setText] = React.useState(ymdToTR(value));
  const textFocused = React.useRef(false);
  React.useEffect(() => {
    if (!textFocused.current) setText(ymdToTR(value));
  }, [value]);

  const handleText = React.useCallback(
    (e) => {
      const t = e.target.value;
      setText(t);
      const ymd = trTextToYmd(t);
      if (ymd) onChange?.(ymd);
      else if (t.trim() === "") onChange?.("");
    },
    [onChange],
  );
  const handleTextBlur = React.useCallback(() => {
    textFocused.current = false;
    setText(ymdToTR(value));
  }, [value]);

  const hasValue = Boolean(value && isValidDateString(value));
  const hasError = Boolean(error);

  return (
    <div className="w-full">
      {label && (
        <Label htmlFor={id} className="text-sm">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        {manualInput ? (
          <div
            className={cn(
              "mt-1 flex items-center gap-1 rounded-md border bg-background px-1",
              hasError ? "border-red-500 bg-red-50" : "border-input",
              !hasValue && required && !hasError && "border-yellow-400 bg-yellow-50",
              buttonClassName,
            )}
          >
            <input
              id={id}
              ref={ref}
              type="text"
              inputMode="numeric"
              disabled={disabled}
              placeholder="GG.AA.YYYY"
              value={text}
              onFocus={() => { textFocused.current = true; }}
              onChange={handleText}
              onBlur={handleTextBlur}
              className="h-8 min-w-0 flex-1 bg-transparent px-1 text-sm focus:outline-none disabled:cursor-not-allowed"
              aria-label={label || "Tarih"}
              {...rest}
            />
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                className="h-7 w-7 shrink-0"
                aria-label="Takvimden seç"
              >
                <CalendarIcon className="h-4 w-4 text-blue-600" />
              </Button>
            </PopoverTrigger>
            {clearable && hasValue && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="mr-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                aria-label="Tarihi temizle"
              >
                <ClearIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        ) : (
          <PopoverTrigger asChild>
            <Button
              id={id}
              ref={ref}
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-start text-left font-normal mt-1",
                !hasValue && "text-muted-foreground",
                !hasValue && required && !hasError && "border-yellow-400 bg-yellow-50",
                hasError && "border-red-500 bg-red-50",
                buttonClassName,
              )}
              {...rest}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-blue-600 shrink-0" />
              <span className="truncate flex-1">{displayLabel}</span>
              {clearable && hasValue && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleClear(e);
                  }}
                  className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                  aria-label="Tarihi temizle"
                >
                  <ClearIcon className="h-3 w-3" />
                </span>
              )}
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent className="w-auto p-0" align={align}>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            {...(manualInput
              ? {
                  month,
                  onMonthChange: setMonth,
                  captionLayout: "dropdown-buttons",
                  fromYear,
                  toYear,
                }
              : { defaultMonth: selectedDate || minDate || new Date() })}
            disabled={disabledMatcher}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            classNames={{
              caption_dropdowns: "flex items-center justify-center gap-1",
              // Native <select> zaten ay/yıl değerini gösteriyor; rdp'nin ayrıca
              // çizdiği değer+ok kopyasını (caption_label) gizle → tekrar önlenir.
              caption_label: "hidden",
              dropdown:
                "rounded-md border border-input bg-white px-1 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer",
              dropdown_month: "relative",
              dropdown_year: "relative",
              day_selected:
                "bg-blue-600 text-white hover:bg-blue-700 hover:text-white focus:bg-blue-700 focus:text-white",
              day_today: "bg-blue-100 text-blue-900 font-bold",
            }}
          />
        </PopoverContent>
      </Popover>
      {helperText && !hasError && (
        <p className="text-xs text-gray-500 mt-1">{helperText}</p>
      )}
      {hasError && (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
});

export { DatePicker };
export default DatePicker;
